import { Building2, Calculator, History, LayoutTemplate } from 'lucide-react';

// The kinds of record the assistant links as zebel://<kind>/<uid>, and the app
// path each opens, on a tab where the kind has them. Mirrors REF_KINDS in
// shiraz-api-v2 `agentConversation/enums.js`; adding a kind means an entry in
// both.
export const AGENT_KINDS = {
  project: {
    label: 'Project',
    icon: Building2,
    // Projects have no page of their own; the database list opens on them.
    path: () => '/project'
  },
  estimate: {
    label: 'Estimate',
    icon: Calculator,
    path: (uid, tab = 'summary') => `/estimate/${uid}/${tab}`
  },
  record: {
    label: 'Record',
    icon: History,
    path: (uid, tab = 'overview') => `/project-record/${uid}/${tab}`
  },
  template: {
    label: 'Template',
    icon: LayoutTemplate,
    path: (uid, tab = 'design') => `/settings/record-template/${uid}/${tab}`
  }
};

// Each record page's tabs, by URL segment, labelled the way its own tab bar
// labels them — so the chip says "Bid Packages", not "bid-package". Also the
// tabs a navigate block may open: the same set as NAVIGATE_TABS in
// shiraz-api-v2 `agentConversation/enums.js`. A project has no tabs.
export const AGENT_TAB_LABELS = {
  project: {},
  estimate: {
    summary: 'Summary',
    'detailed-budget': 'Budget & Inputs',
    'value-engineering': 'VE / Alternates',
    'general-cost-condition': 'GCs/GRs',
    'cost-layout': 'Budget Views',
    'consolidated-design-data': 'Design Data',
    'bid-package': 'Bid Packages',
    analytics: 'Analytics',
    market: 'Market',
    'estimate-report': 'Report',
    compare: 'Compare',
    document: 'Files',
    settings: 'Settings'
  },
  record: {
    overview: 'Overview',
    design: 'Design Data',
    cost: 'Cost Data',
    market: 'Market',
    compare: 'Compare',
    document: 'Files',
    settings: 'Settings'
  },
  template: {
    design: 'Design Data',
    cost: 'WBS',
    'value-engineering': 'VE / Alternates',
    'general-cost-condition': 'GCs/GRs',
    'cost-layout': 'Budget Views',
    'table-view': 'Saved Views',
    'indirect-cost': 'Indirect Costs',
    'tag-group': 'Cost Tags',
    'unit-cost-rate': 'Unit Cost Rates',
    'estimate-programming-config-group': 'Inputs',
    'estimate-overview-config-group': 'Overview',
    'bid-package': 'Bid Packages',
    'estimate-report': 'Report',
    settings: 'Settings'
  }
};

// What a fresh conversation offers to ask. Only things the assistant can read.
// What a fresh conversation offers to ask, by page and tab, then by page.
// Only things the assistant can read: it sees an estimate's totals, cost by
// bucket, quantities, comps, bid packages and VE items; a record's cost and
// design data; a template's WBS, design data, bid packages and VE items. Tabs
// it cannot read (budget views, reports, files, market) fall back to the page.
export const AGENT_SUGGESTED_PROMPTS = {
  'estimate/summary': ['Summarise this estimate', 'What is driving the cost?', 'How does this compare with its comps?'],
  'estimate/detailed-budget': ['Which cost buckets carry most of the budget?', 'What does this cost per unit?', 'Which trades look missing from this budget?'],
  'estimate/value-engineering': ['Which VE items are on this estimate?', 'Which VE items have no alternate selected?', 'Which cost buckets are the biggest VE targets?'],
  'estimate/general-cost-condition': ['How big are the general conditions in this budget?', 'What share of direct cost goes to GCs?', 'Summarise this estimate'],
  'estimate/bid-package': ['Which bid packages have no bid selected yet?', 'Which packages have the fewest bids?', 'How much of the budget is awarded?'],
  'estimate/settings': ['Which comps is this estimate built on?', 'What quantities does it use?', 'What does this cost per unit?'],
  estimate: ['Summarise this estimate', 'What is driving the cost?', 'How does this compare with its comps?'],
  'record/cost': ['Which cost buckets are biggest on this record?', 'How is its cost split across buckets?', 'Which of our estimates are in the same sector?'],
  'record/design': ['What are the key quantities on this record?', 'How big is this project?', 'Which of our estimates are in the same sector?'],
  record: ['Summarise this record', 'What are its biggest cost buckets?', 'Which of our estimates are in the same sector?'],
  'template/cost': ['What does this WBS cover?', 'Which buckets have the most line items?', 'Which bid packages does this template start with?'],
  'template/design': ['What design data does this template ask for?', 'How is its design data grouped?', 'Summarise this template'],
  'template/bid-package': ['Which bid packages does this template start estimates with?', 'How do its bid packages map to its WBS?', 'Summarise this template'],
  'template/value-engineering': ['Which VE items does this template include?', 'Which VE items have the most alternates?', 'Summarise this template'],
  template: ['Summarise this template', 'What does its WBS cover?', 'Which bid packages does it start with?'],
  project: ['How many projects do we have by state?', 'Which estimates were updated this month?', 'Show me our largest estimates'],
  default: ['How many estimates do we have by status?', 'Show me our largest estimates', 'Which projects are in California?']
};

export const AGENT_PANEL_WIDTH = {
  MIN: 340,
  MAX: 720,
  DEFAULT: 420
};

export const AGENT_STORAGE_KEYS = {
  OPEN: 'agent-panel-open',
  WIDTH: 'agent-panel-width',
  CONVERSATION: 'agent-panel-conversation-uid'
};

// The events `POST /agent-message` streams, one JSON object per line.
export const AGENT_STREAM_EVENTS = {
  MESSAGE: 'message',
  TEXT: 'text',
  BLOCK: 'block',
  REFS: 'refs',
  DONE: 'done',
  ERROR: 'error'
};

// What a reply is made of. A type this panel does not know renders nothing.
export const AGENT_BLOCK_TYPES = {
  TEXT: 'text',
  TOOL_STEP: 'tool_step',
  SHOW: 'show',
  ASK: 'ask',
  NAVIGATE: 'navigate'
};

// What a `show` block shows.
export const AGENT_SHOW_TYPES = {
  CARDS: 'cards',
  TABLE: 'table',
  STATS: 'stats',
  BARS: 'bars',
  CALLOUT: 'callout'
};

// The longest page label the API accepts in a message's context.
export const AGENT_CONTEXT_LABEL_MAX_LENGTH = 200;

export const AGENT_MESSAGE_STATUSES = {
  RUNNING: 'running',
  COMPLETE: 'complete',
  STOPPED: 'stopped',
  FAILED: 'failed'
};

export const AGENT_TEXT_MAX_LENGTH = 4000;
export const AGENT_TITLE_MAX_LENGTH = 60;
